import * as Yup from "yup";
import {
  EMAIL_REGEX,
  MOBILE_REGEX,
  NOSPACE_REGEX,
  PASSWORDS_REGEX,
} from "./constants";
import { MESSAGE } from "./validationMessage";
import dayjs from "dayjs";

export let email = Yup.string()
  .max(254, "Email should not be longer than 254 characters")
  .required(MESSAGE.REQUIRED)
  .email(MESSAGE.EMAIL)
  .matches(EMAIL_REGEX, MESSAGE.EMAIL);

let name = Yup.string()
  .min(2, "Name must be at least 2 characters")
  .max(60, "Name should not be longer than 60 characters")
  .required(MESSAGE.REQUIRED)
  .matches(NOSPACE_REGEX, MESSAGE.NO_SPACE);

let title = Yup.string()
  .max(60, "This field should not be longer than 60 characters")
  .required(MESSAGE.REQUIRED)
  .matches(NOSPACE_REGEX, MESSAGE.NO_SPACE);

let description = Yup.string()
  .max(250, "Description should not be longer than 250 characters")
  .required(MESSAGE.REQUIRED)
  .matches(NOSPACE_REGEX, MESSAGE.NO_SPACE);

let question = Yup.string()
  .max(150, "This field should not be longer than 150 characters")
  .required(MESSAGE.REQUIRED)
  .matches(NOSPACE_REGEX, MESSAGE.NO_SPACE);

let status = Yup.mixed().required(MESSAGE.REQUIRED);

let mobile = Yup.string().matches(MOBILE_REGEX, MESSAGE.PHONE);

const editorContentValidation = (cmsData, context) => {
  if (cmsData === undefined || cmsData === null) {
    return context.createError({ message: "Description is required" });
  }

  const plainText = cmsData.replace(/<\/?[^>]*>/g, '').replace(/&nbsp;/g, ' ').replace(/<br\s*[/]?>/gi, '\n').trim();

  if (!plainText) {
    return context.createError({ message: "Description is required" });
  }

  // if (/^\s|^(&nbsp;)+/i.test(cmsData.replace(/<[^>]*>/g, ""))) {
  //   return context.createError({ message: "Description should not start with a space" });
  // }

  if (plainText.length < 10) {
    return context.createError({ message: "Description must be at least 10 characters long" });
  }

  return true;
};


export const CmsSchema = Yup.object({
  title,
  description: Yup.string().test(
    "custom-description-validation",
    "Description is required",
    function (value) {
      return editorContentValidation(value, this);
    }),
  status,
});

const ALPHANUMERIC_WITH_LETTERS_REGEX = /^(?=.*[a-zA-Z])[A-Za-z0-9]*$/;
export const CategorySchema = Yup.object().shape({
  name: Yup.string()
    .required("Category name is required")
    .min(2, "Category name must be at least 2 characters"),
  image: Yup.mixed().required("Image is required"),
  status,
});
export const SubcategorySchema = Yup.object().shape({
  name: Yup.string()
    .required("Sub Category name is required")
    .min(2, "Sub Category name must be at least 2 characters"),
  image: Yup.mixed().required("Image is required"),
  status,
});

export const BidPackageSchema = Yup.object().shape({
  name: Yup.string()
    .required("Name is required")
    .matches(NOSPACE_REGEX, MESSAGE.NO_SPACE),
  price: Yup.number()
    .typeError("Price must be a number")
    .required("Price is required")
    .min(1, "Price must be greater than 0")
    .max(10000, "Price must not exceed 10,000")
    .test("price-format", "Invalid price format", (value) => {
      return /^\d+(\.\d{1,2})?$/.test(value);
    }),
  earnBids: Yup.number()
    .typeError("Earn Bids must be a number")
    .required("Earn Bids is required")
    .min(1, "Earn Bids must be greater than 0")
    .max(10000, "Earn Bids must not exceed 10,000")
    .test("earnBids-format", "Invalid Earn Bids format", (value) => {
      return /^\d+(\.\d{1,2})?$/.test(value);
    }),
  description: Yup.array()
    .of(
      Yup.string()
        .matches(NOSPACE_REGEX, MESSAGE.NO_SPACE)
        .required("Description point cannot be empty")
    )
    .min(1, "At least one description point is required"),
});

export const userSchema = Yup.object().shape({
  name: name,
  // phoneNumber: Yup.string()
  //   .required(MESSAGE.REQUIRED)
  //   .matches(MOBILE_REGEX, MESSAGE.PHONE)
  //   .test("not-all-zeros", MESSAGE.PHONE, (value) => {
  //     return value !== "0000000000";
  //   }),
  email,
});

export const subadminSchema = Yup.object().shape({
  name: name,
  phoneNumber: Yup.string()
    .required(MESSAGE.REQUIRED)
    .matches(MOBILE_REGEX, MESSAGE.PHONE)
    .test("not-all-zeros", MESSAGE.PHONE, (value) => {
      return value !== "0000000000";
    }),
  permissions: Yup.array()
    .of(Yup.string())
    .min(1, "At least one permission is required")
    .required("Permissions are required"),
});

export const TicketSchema = Yup.object().shape({

  name: Yup.string()
    .required("Category name is required")
    .matches(NOSPACE_REGEX, MESSAGE.NO_SPACE)
    .min(2, "Category name must be at least 2 characters"),
  image: Yup.mixed().required("Image is required"),
  venueImage: Yup.mixed().required("Venue image is required"),
  bidPrice: Yup.string()
    .required("Bid start price is required")
    .matches(
      /^(0|[1-9]\d*)(\.\d{1,2})?$/,
      "Bid start price must be a valid number with up to 2 decimal places"
    )
    .test(
      "is-greater-than-zero",
      "Bid start price must be greater than 0",
      function (value) {
        return parseFloat(value || "0") > 0;
      }
    )
    .test(
      "is-less-than-price",
      "Bid start price must be less than the price",
      function (value) {
        const { price } = this.parent;
        return parseFloat(value || "0") < parseFloat(price || "0");
      }
    ),

  price: Yup.string()
    .required("Price is required")
    .matches(
      /^(0|[1-9]\d*)(\.\d{1,2})?$/,
      "Price must be a valid number without leading zeros and up to 2 decimal places"
    )
    .test(
      "is-greater-than-zero",
      "Price must be greater than 0",
      function (value) {
        return parseFloat(value || "0") > 0;
      }
    ),
  totalTicket: Yup.string()
    .required("Total ticket is required")
    .matches(/^[0-9]+$/, "Total ticket must be greater than 0")
    .min(1, "Total ticket must be greater than 0"),
  slug: Yup.string().required("Category type is required"),
  categoryId: Yup.string().required("Category is required"),

  description: Yup.string()
    .trim()
    .required("Description  is required")
    .min(20, "Description name must be at least 20 characters")
    .max(500, "Description should not exceed 500 characters"),

  teamFirst: Yup.string().when("slug", {
    is: (slug) => slug === "sports",
    then: (schema) => schema.required("Team 1 name is required"),
    otherwise: (schema) => schema.notRequired(),
  }),

  teamSecond: Yup.string().when("slug", {
    is: (slug) => slug === "sports",
    then: (schema) => schema.required("Team 2 name is required"),
    otherwise: (schema) => schema.notRequired(),
  }),

  artistName: Yup.string().when("slug", {
    is: (slug) => slug !== "sports",
    then: (schema) => schema.required("Artist name is required"),
    otherwise: (schema) => schema.notRequired(),
  }),

  // New fields
  city: Yup.mixed().required("City is required"),
  state: Yup.string().required("State is required"),
  venue: Yup.string().required("Venue is required"),
  country: Yup.string().required("Country is required"),

  zipcode: Yup.string()
    .required("Zip code is required")
    .matches(/^\d{4,6}$/, "Zip code must be between 4 and 6 digits"),
  fullAddress: Yup.string()
    .required("Full address is required")
    .min(10, "Full address must be at least 10 characters"),
  event_start_date: Yup.date()
    .nullable()
    .required("Event start date is required")
    .min(new Date(), "Event start date cannot be in the past"),

  section: Yup.string()
    .required("Section is required")
    .matches(NOSPACE_REGEX, MESSAGE.NO_SPACE)
    .min(1, "Section must be at least 2 characters"),

  row: Yup.string()
    .required("Row is required")
    .matches(NOSPACE_REGEX, MESSAGE.NO_SPACE)
    .min(1, "Row must be at least 1 characters"),
});

export const AuctionSchema = Yup.object().shape({
  slug: Yup.string().required("Category is required"),

  ticket: Yup.array()
    .min(1, "At least one ticket must be selected")
    .required("Tickets are required"),
  startDate: Yup.date()
    .required("Start date is required")
    .min(new Date(), "Start date cannot be in the past"),

  endDate: Yup.date()
    .required("End date is required")
    .min(Yup.ref("startDate"), "End date cannot be before start date"),
});

export const FreeBidManagementSchema = Yup.object().shape({
  type: Yup.string()
    .required("Type is required.")
    .oneOf(["daily", "weekly"], "Invalid type selected."),
  freeBid: Yup.number()
    .required("Free bids are required.")
    .positive("Free bids must be greater than 0"),
  videoLength: Yup.number()
    .required("Video length is required.")
    .min(5, "Video length must be greater than 5 seconds")
    .max(60, "Video length should not be longer than 60 seconds"),
  description: Yup.string()
    .required(MESSAGE.REQUIRED)
    .matches(NOSPACE_REGEX, MESSAGE.NO_SPACE)
    .min(3, "Description must be at least 3 characters")
    .max(50, "Description should not be longer than 250 characters"),
  thumbnailImage: Yup.mixed().required("Thumbnail image is required."),
  video: Yup.mixed().required("Video is required."),
  status,
});

export const AuctionTaskSchema = Yup.object().shape({
  title: Yup.string()
    .required("Title is required")
    .matches(NOSPACE_REGEX, MESSAGE.NO_SPACE)
    .min(2, "Title name must be at least 2 characters")
    .max(100, "Title cannot exceed 100 characters"),

  purchaseRange: Yup.number()
    .typeError("Purchase range must be a number")
    .required("Purchase range is required")
    .min(0.01, "Purchase range must be at least 0.01")
    .max(9999, "Purchase range cannot exceed 9999"),

  freeBidsGranted: Yup.number()
    .typeError("Free bids must be a number")
    .required("Free bids are required")
    .min(0.01, "Free bids must be at least 0.01")
    .max(500, "Free bids cannot exceed 500"),

  notice: Yup.string()
    .required("Notice is required")
    .min(10, "Notice must be at least 10 characters long"),

  image: Yup.mixed().test(
    "required",
    "Image is required",
    (value) => value instanceof File || !!value
  ),

  status: Yup.boolean().required("Status is required"),
});

export const ShareTaskSchema = Yup.object().shape({
  title: Yup.string()
    .matches(NOSPACE_REGEX, MESSAGE.NO_SPACE)
    .min(2, "Title name must be at least 2 characters")
    .required("Title is required")
    .max(100, "Title must not exceed 100 characters"),
  shareNumber: Yup.number()
    .typeError("Share Number must be a valid integer")
    .integer("Share Number must be a whole number")
    .required("Share Number is required")
    .min(1, "Share range must be at least 1")
    .max(9999, "Share range cannot exceed 9999"),
  freeBidsGranted: Yup.number()
    .required("Free Bids is required")
    .min(0.01, "Free Bids must be at least 0.01")
    .max(500, "Free Bids cannot exceed 500"),
  notice: Yup.string()
    .required("Notice is required")
    .min(10, "Notice must be at least 10 characters long"),
  image: Yup.mixed().test(
    "required",
    "Image is required",
    (value) => value instanceof File || !!value
  ),

  status: Yup.boolean().required("Status is required"),
});

export const PlaceBidTaskSchema = Yup.object().shape({
  title: Yup.string()
    .matches(NOSPACE_REGEX, MESSAGE.NO_SPACE)
    .min(2, "Title name must be at least 2 characters")
    .required("Title is required")
    .max(9999, "Title must not exceed 9999 characters"),
  shareBids: Yup.number()
    .typeError("Place Bids must be a valid integer")
    .integer("Place Bids must be a whole number")
    .required("Place Bids is required")
    .min(1, "Purchase range must be at least 1")
    .max(1000, "Purchase range cannot exceed 100"),
  freeBidsGranted: Yup.number()
    .required("Free Bids is required")
    .min(0.01, "Free Bids must be at least 0.01")
    .max(500, "Free Bids cannot exceed 500"),
  notice: Yup.string()
    .required("Notice is required")
    .min(10, "Notice must be at least 10 characters long"),
  image: Yup.mixed().test(
    "required",
    "Image is required",
    (value) => value instanceof File || !!value
  ),

  status: Yup.boolean().required("Status is required"),
});
